import type { OpdListFilters, OpdLookupOption, PatientFormFields } from './types';
import { doctorOptionLabel } from '../../lib/doctor-name';
import { normalizePhoneInput } from '../../lib/phone-input';

export const initialOpdAppointmentFilters = (): OpdListFilters => ({ page: 1, limit: 10 });

const opdDateParts = (value: string | number | Date) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return { day: get('day'), month: get('month'), year: get('year'), hour: get('hour'), minute: get('minute'), dayPeriod: get('dayPeriod').toUpperCase() };
};

export const formatOpdDateTime = (value: string | number | Date) => {
  const parts = opdDateParts(value);
  return parts ? `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute} ${parts.dayPeriod}` : '-';
};

export const formatOpdAppointmentDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('day')}-${get('month')}-${get('year')} ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`;
};

export const formatOpdDate = (value: string | number | Date) => {
  const parts = opdDateParts(value);
  return parts ? `${parts.day}-${parts.month}-${parts.year}` : '-';
};

export const formatOpdScheduleDateTime = (dateValue: string, timeValue?: string) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '-';
  const dateParts = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata',
  }).formatToParts(date);
  const getDatePart = (type: Intl.DateTimeFormatPartTypes) => dateParts.find((part) => part.type === type)?.value ?? '';
  const time = timeValue?.slice(0, 5) ?? '00:00';
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return '-';
  const hour = Number(match[1]);
  const minute = match[2];
  const displayHour = String(hour % 12 || 12).padStart(2, '0');
  const dayPeriod = hour >= 12 ? 'PM' : 'AM';
  return `${getDatePart('day')}-${getDatePart('month')}-${getDatePart('year')}, ${displayHour}:${minute} ${dayPeriod}`;
};

export const formatOpdScheduleDate = (dateValue: string) => formatOpdScheduleDateTime(dateValue).split(',')[0] ?? '-';
export const formatOpdScheduleTime = (dateValue: string, timeValue: string) => (formatOpdScheduleDateTime(dateValue, timeValue).split(', ')[1] ?? '-').toUpperCase();

export const currency = (value: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);

const amountWordsOnes = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const amountWordsTens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const amountWordsTwoDigits = (value: number): string => {
  if (value < 20) return amountWordsOnes[value] ?? '';
  const tens = amountWordsTens[Math.floor(value / 10)] ?? '';
  const ones = value % 10 ? ` ${amountWordsOnes[value % 10]}` : '';
  return `${tens}${ones}`;
};

const amountWordsThreeDigits = (value: number): string => {
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  return [hundreds ? `${amountWordsOnes[hundreds]} Hundred` : '', rest ? amountWordsTwoDigits(rest) : ''].filter(Boolean).join(' ');
};

/** Indian-numbering (crore/lakh/thousand) amount-in-words, used on printed receipts. */
export const amountInWords = (amount: number): string => {
  const value = Math.floor(Math.abs(amount));
  if (value === 0) return 'Zero Rupees Only';
  const crore = Math.floor(value / 1_00_00_000);
  const lakh = Math.floor((value % 1_00_00_000) / 1_00_000);
  const thousand = Math.floor((value % 1_00_000) / 1000);
  const hundred = value % 1000;
  const parts = [
    crore ? `${amountWordsThreeDigits(crore)} Crore` : '',
    lakh ? `${amountWordsTwoDigits(lakh)} Lakh` : '',
    thousand ? `${amountWordsTwoDigits(thousand)} Thousand` : '',
    hundred ? amountWordsThreeDigits(hundred) : '',
  ].filter(Boolean);
  return `${parts.join(' ')} Rupees Only`;
};

export const todayInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
};

export const dateTimeInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

export const optionList = (items: OpdLookupOption[] | undefined) => (items ?? []).map((item) => ({ value: item.id, label: doctorOptionLabel(item.label, item.code) }));

export const ageFromDateOfBirth = (dob: string): Pick<PatientFormFields, 'ageYears' | 'ageMonths' | 'ageDays'> => {
  const birth = new Date(dob);
  const today = new Date();
  let ageYears = today.getFullYear() - birth.getFullYear();
  let ageMonths = today.getMonth() - birth.getMonth();
  let ageDays = today.getDate() - birth.getDate();
  if (ageDays < 0) {
    ageMonths -= 1;
    ageDays += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
  }
  if (ageMonths < 0) {
    ageYears -= 1;
    ageMonths += 12;
  }
  return { ageYears: Math.max(0, ageYears), ageMonths: Math.max(0, ageMonths), ageDays: Math.max(0, ageDays) };
};

export const blankPatientFields = (): PatientFormFields => ({
  patientId: null, uhid: null, name: '', phone: null, gender: '', dateOfBirth: null, ageYears: 0, ageMonths: 0, ageDays: 0,
  address: null, state: 'West Bengal (WB)', district: null, pinCode: null,
});

export const patientFieldsFromOption = (option: OpdLookupOption): PatientFormFields => {
  const age = option.dateOfBirth ? ageFromDateOfBirth(option.dateOfBirth) : { ageYears: 0, ageMonths: 0, ageDays: 0 };
  return {
    patientId: option.id,
    uhid: option.code ?? null,
    name: option.label,
    phone: option.phone ? normalizePhoneInput(option.phone) : null,
    gender: option.gender === 'female' ? 'Female' : option.gender === 'male' ? 'Male' : 'Others',
    dateOfBirth: option.dateOfBirth?.slice(0, 10) ?? null,
    ...age,
    address: option.address ?? null,
    state: option.state ?? 'West Bengal (WB)',
    district: option.district ?? null,
    pinCode: option.pinCode ?? null,
  };
};
